import {
  useState,
  useEffect,
  useContext,
  createContext,
  ReactNode,
  useMemo,
} from 'react';
import { type ContextInterface } from '@convertcom/js-sdk';

import convertSdk from '../sdk/convertSdkClient';

import { useCookies } from 'hooks';

import { useUserContext } from './UserContext';

import { PAGE } from '../helpers/pages';
import { logger } from '@dist-marketplace-frontend/services';
import { COOKIE } from '../helpers/constants';
import { PRICING_INDICATOR_EXPERIMENT_KEY } from '../utils';
import { pushToDataLayer } from '../services/gtmService';

const PRICING_INDICATOR_BUCKETED_KEY = `${PRICING_INDICATOR_EXPERIMENT_KEY}-bucketed`;

type UseConvertContextParameters = { page: PAGE };

type ConvertProviderParameters = {
  children: ReactNode;
  showPricingIndicator?: boolean;
  pricingIndicatorVariationKey?: string | null;
};

const ConvertContext = createContext<{
  sdkContext: ContextInterface | null | undefined;
  showPricingIndicator: boolean;
} | null>(null);
/**
 * This function allows the Convert SDK context to be accessed anywhere within the application
 * in order to run A/B experiments that require code changes.
 *
 * This may include displaying different components and executing different functions
 * depending on variations within the experiment.
 */
const ConvertProvider = (params: ConvertProviderParameters) => {
  const {
    children,
    showPricingIndicator = false,
    pricingIndicatorVariationKey = null,
  } = params;

  const { get } = useCookies({ name: COOKIE.DD_CLIENT_ID });

  const ddClientId = get();

  const [sdkContext, setSdkContext] = useState<
    ContextInterface | null | undefined
  >();
  const {
    consentOptions: { allowConvert },
  } = useUserContext();

  useEffect(() => {
    const initSdk = async (userId: string) => {
      try {
        await convertSdk.onReady();
        const context = convertSdk.createContext(userId);
        setSdkContext(context);
      } catch (error) {
        setSdkContext(null);
        logger.error({
          error,
          message: 'Error loading Convert SDK',
          tags: [PAGE.APP],
        });
      }
    };
    if (
      allowConvert &&
      typeof ddClientId === 'string' &&
      ddClientId.trim().length > 0
    ) {
      initSdk(ddClientId);
    } else {
      setSdkContext(null);
    }
  }, [allowConvert, ddClientId]);

  // The pricing indicator experiment is bucketed server-side to avoid a flash of content —
  // the variation must be known before the page renders. Because of this, the Convert client
  // SDK never calls runExperience for it, so the SDK's native bucketing event never fires.
  // This effect is the workaround: it pushes the GTM experiment_bucketed event client-side
  // based on the server-determined pricingIndicatorVariationKey prop. localStorage is used to
  // deduplicate across page loads, since this effect runs on every mount where the user
  // has been bucketed (both control and variant).
  useEffect(() => {
    if (!pricingIndicatorVariationKey) return;

    try {
      if (localStorage.getItem(PRICING_INDICATOR_BUCKETED_KEY)) return;
      localStorage.setItem(PRICING_INDICATOR_BUCKETED_KEY, 'true');
      pushToDataLayer({
        event: 'experiment_bucketed',
        experimentKey: PRICING_INDICATOR_EXPERIMENT_KEY,
        variationKey: pricingIndicatorVariationKey,
      });
    } catch (error) {
      logger.error({
        error,
        message:
          'Failed to track experiment_bucketed — localStorage unavailable',
        tags: [PAGE.APP],
      });
    }
  }, [pricingIndicatorVariationKey]);

  const contextValue = useMemo(
    () => ({
      sdkContext,
      showPricingIndicator,
    }),
    [sdkContext, showPricingIndicator],
  );

  return (
    <ConvertContext.Provider value={contextValue}>
      {children}
    </ConvertContext.Provider>
  );
};

const useConvertContext = (params: UseConvertContextParameters) => {
  const { page } = params;
  const context = useContext(ConvertContext);
  if (!context) {
    const error = new Error(
      'useConvertContext must be used within an instance of ConvertProvider',
    );
    logger.error({
      error,
      message:
        'useConvertContext must be used within an instance of ConvertProvider',
      tags: [page],
    });
    throw error;
  }
  return context;
};

export { ConvertProvider, useConvertContext, ConvertContext };
